import type { ReactNode } from "react";
import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  Link,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { ThemeProvider } from "next-themes";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { NewsProvider } from "@/context/NewsContext";
import { PodcastPlayerProvider } from "@/context/PodcastPlayerContext";
import { DailyRecapPlayerProvider } from "@/context/DailyRecapPlayerContext";
import { ArticleAudioPlayerProvider } from "@/context/ArticleAudioPlayerContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import NotFound from "@/pages/NotFound";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";
import WebMcpProvider from "@/components/webmcp/WebMcpProvider";
import { useVisitTracker } from "@/hooks/useVisitTracker";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import {
  DEFAULT_LOCALE,
  HREFLANGS,
  HTML_LANG,
  LOCALES,
  localizePath,
  type Locale,
} from "@/i18n/config";
import { LocaleProvider } from "@/i18n/useLocale";
import type { LocaleRef } from "@/router";

const SITE_ORIGIN = "https://www.thebyteline.com";
// Persist only the article feed so returning visitors repaint instantly from
// localStorage while the fresh page revalidates in the background.
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "byteline-query-cache",
  throttleTime: 1000,
});

const persistOptions = {
  persister,
  maxAge: 60 * 60 * 1000, // 1 hour
  buster: "v1",
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) =>
      query.queryKey[0] === "articles" && query.queryKey[1] === "feed",
  },
};

const PageViewTracker = () => {
  usePageViewTracker();
  useVisitTracker();
  return null;
};

const LEGACY_CACHE_RELOAD_KEY = "byteline-legacy-cache-cleared";

function useLegacyCacheCleanup() {
  useEffect(() => {
    const clearLegacyState = async () => {
      if (!("serviceWorker" in navigator) || !("caches" in window)) return;

      const hadController = navigator.serviceWorker.controller !== null;
      const registrations = await navigator.serviceWorker.getRegistrations();
      const cacheNames = await caches.keys();
      const legacyCacheNames = cacheNames.filter((name) =>
        /(^|-)precache-v\d+-|(^|-)runtime-|(^|-)googleAnalytics-/.test(name),
      );

      if (registrations.length === 0 && legacyCacheNames.length === 0) return;

      await Promise.allSettled(registrations.map((registration) => registration.unregister()));
      await Promise.allSettled(legacyCacheNames.map((name) => caches.delete(name)));

      if (hadController && sessionStorage.getItem(LEGACY_CACHE_RELOAD_KEY) !== "1") {
        sessionStorage.setItem(LEGACY_CACHE_RELOAD_KEY, "1");
        window.location.reload();
      }
    };

    void clearLegacyState().catch(() => {
      // Cache cleanup is best-effort and must never interrupt the page.
    });
  }, []);
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  localeRef: LocaleRef;
}>()({
  head: (ctx) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Byteline - Technology News & Market Intelligence" },
      {
        name: "description",
        content:
          "Daily intelligence on disruptive technology (AI, semiconductors, robotics, space, energy and markets) with curated news, briefs and audio recaps.",
      },
      { name: "author", content: "Byteline" },
      { name: "theme-color", content: "#000000" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Byteline" },
      { property: "og:title", content: "Byteline - Technology News & Market Intelligence" },
      {
        property: "og:description",
        content:
          "Daily intelligence on disruptive technology: AI, semiconductors, robotics, space, energy and markets.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Byteline" },
      { property: "og:url", content: "https://www.thebyteline.com/" },
      { property: "og:image", content: "https://www.thebyteline.com/icon.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Byteline - Technology News & Market Intelligence" },
      {
        name: "twitter:description",
        content:
          "Daily intelligence on disruptive technology: AI, semiconductors, robotics, space, energy and markets.",
      },
      { name: "twitter:image", content: "https://www.thebyteline.com/icon.png" },
    ],
    links: [
      // Start the above-the-fold fonts with the HTML rather than waiting for
      // their declarations in the application stylesheet.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/MriyaGrotesk-Regular.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/MriyaGrotesk-Bold.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/png", href: "/icon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "preconnect", href: "https://koggqbieznrgretnljpw.supabase.co" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://api.oddpool.com" },
      ...localeAlternateLinks(ctx),
    ],
    styles: [
      {
        // This cover is present in the first HTML response, so a language
        // change cannot expose the page before the application styles are ready.
        children:
          "#byteline-boot-loader{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:14px;background:#fff;color:#111;font:14px system-ui,sans-serif}" +
          "#byteline-boot-spinner{width:26px;height:26px;border-radius:9999px;border:2px solid rgba(0,0,0,.15);border-top-color:#111;animation:byteline-boot-spin .7s linear infinite}" +
          "@keyframes byteline-boot-spin{to{transform:rotate(360deg)}}",
      },
    ],
    scripts: [
      {
        // If styles fail, keep the clean cover and explain the delay instead
        // of revealing an unusable, unstyled page.
        children:
          "setTimeout(function(){try{var l=document.getElementById('byteline-boot-loader'),e=document.getElementById('byteline-boot-label');if(l&&e&&getComputedStyle(l).display!=='none'){e.textContent='This is taking longer than expected. Please refresh.';}}catch(e){}},10000);",
      },
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-0NM3CXGDBK');" +
          "(function(){var l=false;function load(){if(l)return;l=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-0NM3CXGDBK';document.head.appendChild(s);}" +
          "['pointerdown','keydown','scroll','touchstart'].forEach(function(e){window.addEventListener(e,load,{once:true,passive:true});});" +
          "window.addEventListener('load',function(){setTimeout(load,4000);},{once:true});})();",
      },
      {
        type: "application/ld+json",
        children:
          '{"@context":"https://schema.org","@type":"NewsMediaOrganization","@id":"https://www.thebyteline.com/#organization","name":"Byteline","url":"https://www.thebyteline.com","logo":{"@type":"ImageObject","url":"https://www.thebyteline.com/icon.png","width":512,"height":512},"publishingPrinciples":"https://www.thebyteline.com/editorial-standards","sameAs":["https://twitter.com/thebyteline"]}',
      },
      {
        type: "application/ld+json",
        children:
          '{"@context":"https://schema.org","@type":"WebSite","name":"Byteline","url":"https://www.thebyteline.com","potentialAction":{"@type":"SearchAction","target":"https://www.thebyteline.com/search?q={search_term_string}","query-input":"required name=search_term_string"}}',
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

/** hreflang alternates for the page currently being rendered. */
function localeAlternateLinks(ctx: {
  match?: { context?: unknown };
  matches?: { pathname?: string }[];
}) {
  const matches = ctx.matches ?? [];
  const path = matches[matches.length - 1]?.pathname ?? "/";
  const clean = path.length > 1 ? path.replace(/\/$/, "") : "/";
  const links: { rel: string; href: string; hrefLang?: string }[] = [];
  for (const locale of LOCALES) {
    const href = `${SITE_ORIGIN}${localizePath(clean, locale)}`;
    for (const hrefLang of HREFLANGS[locale]) links.push({ rel: "alternate", hrefLang, href });
  }
  links.push({ rel: "alternate", hrefLang: "x-default", href: `${SITE_ORIGIN}${clean}` });
  return links;
}

function useActiveLocale(): Locale {
  const router = useRouter();
  const context = router.options.context as { localeRef?: LocaleRef } | undefined;
  return context?.localeRef?.current ?? DEFAULT_LOCALE;
}

function RootShell({ children }: { children: ReactNode }) {
  const locale = useActiveLocale();
  return (
    <html lang={HTML_LANG[locale]} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <div id="byteline-boot-loader" role="status" aria-live="polite">
          <div id="byteline-boot-spinner" aria-hidden="true" />
          <span id="byteline-boot-label">Loading…</span>
        </div>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const locale = useActiveLocale();
  useLegacyCacheCleanup();
  return (
    <LocaleProvider locale={locale}>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light">
        <TooltipProvider>
            <AuthProvider>
              <ArticleAudioPlayerProvider>
                <PodcastPlayerProvider>
                  <DailyRecapPlayerProvider>
                    <NewsProvider>
                      <Toaster />
                      <PageViewTracker />
                      <WebMcpProvider />
                      <ErrorBoundary>
                        <Outlet />
                      </ErrorBoundary>
                    </NewsProvider>
                  </DailyRecapPlayerProvider>
                </PodcastPlayerProvider>
              </ArticleAudioPlayerProvider>
            </AuthProvider>
          </TooltipProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
    </LocaleProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-semibold">This page didn't load</h1>
        <p className="text-muted-foreground">Something went wrong while loading this page.</p>
        <div className="flex items-center justify-center gap-3">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link to="/" className="px-4 py-2 rounded-md border border-border">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}
